import { useEffect, useState } from 'react';
import { Flip } from 'gsap/Flip';
import gsap from 'gsap';
import Eye from '../Eye';
import { SequenceItem } from './OnboardingSequence';
import Welcome from './Welcome';
import useOnboardingStore from 'stores/onboardingStore';
import { useAuth } from 'context/auth.context';
import { useGSAP } from '@gsap/react';
import {
  ChooseSignupMethodInstruction,
  EmailInputBar,
} from './ChooseSignupMethod';
import { MAP_ONBOARDING_PHASE_TO_ROUTE } from 'utils';
import { useNavigate } from 'react-router-dom';
import BPToast from 'components/BionicUI/toast/BPToast';
import useNotifyStore from 'stores/notifyStore';
import { FirebaseError } from 'firebase/app';
import { OnboardingPhases } from 'core/models/users/user.model';
import { usePrivy } from '@privy-io/react-auth';
import { ROUTES } from 'routes';
// import { ROUTES } from 'routes';

gsap.registerPlugin(Flip);

type AnimState = {
  eyeHarness: string;
  eyeBorder: boolean;
};

const anim = {
  from: {
    eyeHarness:
      'grid grid-rows-[1fr_auto_1fr] gap-[30px] absolute top-0 left-0 w-full h-full place-content-center',
    eyeBorder: false,
  },
  to: {
    eyeHarness: 'block relative w-[40px] h-[40px] rounded-[50%]', //  border-[4px] border-gray-600'
    eyeBorder: true,
  },
};

const Intro = () => {
  const { handleProfileCreationOrLoad } = useAuth();
  const [showInputBar, setShowInputBar] = useState<boolean>(false);
  const [showInstruction, setShowInstruction] = useState<boolean>(false);
  const [showDaoIntro, setShowDaoIntro] = useState<boolean>(false);
  const [style, setStyle] = useState<AnimState>(anim.from);
  const [flipState, setFlipState] = useState<Flip.FlipState>(
    {} as Flip.FlipState
  );
  const { fadeVaultIn, setProgressStep, progressStep } = useOnboardingStore();
  const navigate = useNavigate();
  const { setCurrentToast } = useNotifyStore();
  const { user, authenticated, login } = usePrivy();
  // console.log('user on landing', user);

  const animate = () => {
    const state = Flip.getState('#eye-video');
    setFlipState(state);
    setStyle(anim.to);
  };

  useGSAP(() => {
    gsap.from('#eye-video', {
      alpha: 0,
      scale: 1.4,
      duration: 1,
      ease: 'power3.out',
      onComplete: () => {
        setShowDaoIntro(true);
      },
    });
  }, []);

  useGSAP(() => {
    if (style !== anim.to) return;

    fadeVaultIn();
    Flip.from(flipState, {
      duration: 1,
      ease: 'power3.inOut',
      onComplete: () => {
        setShowInstruction(true);
      },
    });
  }, [style]);

  useEffect(() => {
    const redirectUser = async () => {
      const response = await handleProfileCreationOrLoad();
      if (!!response) {
        if (response.onboardingPhase === OnboardingPhases.SIGNUP) {
          setProgressStep(2);
        }
        if (
          response.roles?.includes('PROJECT_OWNER') &&
          response.onboardingPhase === OnboardingPhases.DONE
        ) {
          navigate(ROUTES.APPLICATION_IN_REVIEW);
        } else {
          navigate(MAP_ONBOARDING_PHASE_TO_ROUTE[response.onboardingPhase]);
        }
      }
    };

    if (user && authenticated) {
      // console.log('&&&&&&&&&&');
      fadeVaultIn();
      const xStep = localStorage.getItem('currentStep');
      if (xStep) {
        setProgressStep(JSON.parse(xStep));
        localStorage.removeItem('currentStep');
        localStorage.setItem('fromIntro', JSON.stringify(true));
      } else {
        redirectUser();
      }
      // If user is logged in, navigate to the desired logged-in route
      // navigate(ROUTES.DASHBOARD); // or any route for authenticated users
    }
  }, [user, authenticated, navigate]);

  async function handleSignIn(methods: ['email'] | ['google']) {
    try {
      login({ loginMethods: methods });
    } catch (error) {
      const errMsg = (error as FirebaseError).message || 'Try again later.';

      setCurrentToast(<BPToast message={errMsg} variant="error" />);
    }
  }

  return (
    <>
      <SequenceItem progressStep={progressStep}>
        <div className="inline-grid relative w-full max-w-[800px] px-6 grid-cols-[auto_1fr] gap-[20px] mx-auto pt-[15vh] items-start">
          <div className={style.eyeHarness}>
            <div />
            <Eye showBorder={style.eyeBorder} />
            {showDaoIntro && <Welcome onAnimComplete={animate} />}
          </div>
          {showInstruction && (
            <ChooseSignupMethodInstruction
              setShowInputBar={setShowInputBar}
              delay={1300}
            />
          )}
        </div>
        {showInputBar && <EmailInputBar handleSignIn={handleSignIn} />}
      </SequenceItem>
    </>
  );
};

export default Intro;
